import { motion } from 'framer-motion'
import { Satellite, Monitor, Smartphone, Cpu, Cloud, Zap } from 'lucide-react'

const Services = () => {
  const services = [
    {
      icon: Satellite,
      title: 'IoT Systems',
      description: 'Smart sensors, edge devices, and cloud dashboards for real-time monitoring.',
      features: ['Sensor Integration', 'Edge Computing', 'Real-time Analytics'],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Monitor,
      title: 'Web Applications',
      description: 'Dynamic and secure web dashboards & portals with modern frameworks.',
      features: ['Responsive Design', 'API Integration', 'Cloud Deployment'],
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Smartphone,
      title: 'Mobile Apps',
      description: 'Cross-platform mobile solutions with sleek UI for Android & iOS.',
      features: ['Native Performance', 'Push Notifications', 'Offline Support'],
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Cpu,
      title: 'Hardware Solutions',
      description: 'Custom embedded systems and microcontroller programming.',
      features: ['Arduino/ESP32', 'PCB Design', 'Prototyping'],
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: Cloud,
      title: 'Cloud Integration',
      description: 'Seamless cloud connectivity with AWS, Azure, and Firebase.',
      features: ['Data Storage', 'API Services', 'Scalability'],
      color: 'from-indigo-500 to-blue-500'
    },
    {
      icon: Zap,
      title: 'Automation',
      description: 'Smart automation solutions for homes, offices, and industries.',
      features: ['Home Automation', 'Process Control', 'Remote Monitoring'],
      color: 'from-yellow-500 to-orange-500'
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  }

  return (
    <section id="services" className="py-20 px-6 bg-slate-900/50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
              Our Expertise
            </span>
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Full-stack development from sensor to screen, delivering end-to-end solutions.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ scale: 1.05, y: -5 }}
              className="glass-card p-6 hover:bg-white/10 transition-all duration-300 group"
            >
              <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${service.color} mb-4`}>
                <service.icon size={28} className="text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3 group-hover:text-blue-400 transition-colors">
                {service.title}
              </h3>
              <p className="text-sm text-white/70 mb-4 leading-relaxed">
                {service.description}
              </p>
              <ul className="space-y-2">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="text-xs text-white/60 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export default Services
